import { useEffect, useState } from 'react';
import type { PoseLandmarker } from '@mediapipe/tasks-vision';
import { initPoseLandmarkerForVideo } from '@/lib/mediaPipeService';
import type { Landmark } from '@/components/posture-analysis/types';
import logger, { LogCategory } from '@/lib/logging';

/**
 * Live MediaPipe pose on a <video> (the shared call window). Returns landmarks in video pixel
 * coordinates, the same shape the posture capture's realtime overlay draws. Stops when disabled.
 */
export function useLivePose(video: HTMLVideoElement | null, enabled: boolean): Landmark[] | null {
  const [landmarker, setLandmarker] = useState<PoseLandmarker | null>(null);
  const [landmarks, setLandmarks] = useState<Landmark[] | null>(null);

  useEffect(() => {
    if (!enabled || landmarker) return;
    let cancelled = false;
    initPoseLandmarkerForVideo()
      .then((lm) => {
        if (!cancelled) setLandmarker(lm);
      })
      .catch((err: unknown) => {
        logger.error('RemoteAssessment: pose model failed to load', {
          category: LogCategory.CAMERA,
          errorMessage: (err as Error)?.message,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, landmarker]);

  useEffect(() => {
    if (!enabled || !landmarker || !video) {
      setLandmarks(null);
      return;
    }
    let frame = 0;
    let lastTime = -1;
    const detect = () => {
      if (video.videoWidth > 0 && video.videoHeight > 0 && video.currentTime !== lastTime) {
        lastTime = video.currentTime;
        try {
          const result = landmarker.detectForVideo(video, performance.now());
          const raw = result.landmarks[0];
          setLandmarks(
            raw
              ? raw.map((p) => ({ x: p.x * video.videoWidth, y: p.y * video.videoHeight, z: p.z, visibility: p.visibility }))
              : null,
          );
        } catch (err) {
          logger.debug('RemoteAssessment: live pose frame skipped', {
            category: LogCategory.CAMERA,
            errorMessage: (err as Error)?.message,
          });
        }
      }
      frame = requestAnimationFrame(detect);
    };
    frame = requestAnimationFrame(detect);
    return () => cancelAnimationFrame(frame);
  }, [enabled, landmarker, video]);

  return enabled ? landmarks : null;
}
